/**
 * Canvas-based image preprocessing — upscale, grayscale, contrast stretch,
 * mild unsharp mask. Returns a PNG Blob optimised for OCR.
 */
const preprocessImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d", { willReadFrequently: true });

      const TARGET_WIDTH = 1500;
      let width = img.width;
      let height = img.height;

      if (width < TARGET_WIDTH) {
        const scale = TARGET_WIDTH / width;
        width = TARGET_WIDTH;
        height = Math.round(height * scale);
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      const imageData = ctx.getImageData(0, 0, width, height);
      const d = imageData.data;

      // Pass 1: grayscale
      const gray = new Uint8Array(width * height);
      for (let i = 0, j = 0; i < d.length; i += 4, j++) {
        gray[j] = Math.round(0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]);
      }

      // Pass 2: histogram bounds
      let min = 255;
      let max = 0;
      for (let i = 0; i < gray.length; i++) {
        if (gray[i] < min) min = gray[i];
        if (gray[i] > max) max = gray[i];
      }
      const range = max - min || 1;

      // Pass 3: contrast stretch + mild unsharp mask
      for (let i = 0, j = 0; i < d.length; i += 4, j++) {
        let val = ((gray[j] - min) / range) * 255;
        if (j > width && j < gray.length - width) {
          const localAvg =
            (gray[j - 1] + gray[j + 1] + gray[j - width] + gray[j + width]) / 4;
          val = val + (val - localAvg) * 0.3;
        }
        val = Math.max(0, Math.min(255, val));
        d[i] = d[i + 1] = d[i + 2] = val;
      }

      ctx.putImageData(imageData, 0, 0);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(objectUrl);
          resolve(blob);
        },
        "image/png",
        0.95
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load image for preprocessing"));
    };

    img.src = objectUrl;
  });
};

export default preprocessImage;
